import React, { useState, useEffect, useRef } from 'react';
import {

  Box,
  Button,
  Typography,
  Alert,
  useTheme,
  CircularProgress,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import { Google, Apple, Email, ExpandMore } from '@mui/icons-material';
// Use new unified Firebase manager
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, User, OAuthProvider, signInWithEmailAndPassword } from 'firebase/auth';
// Web-specific Firebase for OAuth to avoid cross-platform conflicts
import { initializeWebAuth, getWebGoogleProvider, getWebAuth, getWebAppleProvider } from '../../services/webFirebaseAuth';
import { Logo } from '../widgets/Logo';
import { isNative, isIOS } from '../../capacitor-init';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { signInWithCredential } from 'firebase/auth';
import { font } from '../../utils/fontHelper';

interface LoginScreenProps {
  onLoginSuccess: (user: User) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'apple' | 'email' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const redirectResultChecked = useRef(false);

  // Determine if Apple Sign-In should be shown
  // Show on native iOS or macOS browsers
  const showAppleSignIn = (): boolean => {
    if (isNative() && isIOS()) return true;
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMac = userAgent.includes('macintosh') || userAgent.includes('mac os');
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      return isMac || isIOSDevice;
    }
    return false;
  };

  // Utility function to clear all authentication-related browser data
  const clearAllAuthData = () => {
    if (typeof window !== 'undefined') {
      // Clear all localStorage
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.startsWith('firebase:') ||
          key.includes('auth') ||
          key.includes('google') ||
          key.includes('oauth')) {
          localStorage.removeItem(key);
        }
      });

      // Clear all sessionStorage
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach(key => {
        if (key.startsWith('firebase:') ||
          key.includes('auth') ||
          key.includes('google') ||
          key.includes('oauth')) {
          sessionStorage.removeItem(key);
        }
      });

      console.log('🧹 Cleared all authentication data from browser storage');
    }
  };

  // Handle redirect result on component mount
  useEffect(() => {
    // Guard against React strict-mode double-invoke and re-mounts while a popup
    // is in flight — calling getRedirectResult a second time while a PopupOperation
    // is pending throws "INTERNAL ASSERTION FAILED: Pending promise was never set".
    if (redirectResultChecked.current) return;
    redirectResultChecked.current = true;

    // Native Capacitor platforms use the plugin directly — no redirect flow exists.
    // Calling getRedirectResult with indexedDB persistence throws auth/argument-error.
    if (isNative()) {
      setLoading(false);
      return;
    }

    const handleRedirectResult = async () => {
      try {
        console.log('🔍 Checking for redirect result...');
        // Must use the same auth instance that initiated the redirect to avoid
        // "Pending promise was never set" internal assertion errors in Firebase Auth.
        const { auth } = initializeWebAuth();

        // Add a small delay to ensure Firebase is fully ready
        await new Promise(resolve => setTimeout(resolve, 100));

        const result = await getRedirectResult(auth);

        if (result?.user) {
          console.log('🎉 Redirect authentication successful! User details:', {
            email: result.user.email,
            displayName: result.user.displayName,
            uid: result.user.uid,
            photoURL: result.user.photoURL
          });
          onLoginSuccess(result.user);
        } else {
          console.log('📝 No redirect result found - user hasn\'t completed authentication yet');
          setLoading(false);
        }
      } catch (error: any) {
        console.error('🚨 Redirect result error:', error);

        // Handle specific redirect errors and Firebase internal assertion errors
        // that fire when getRedirectResult is called while a popup is still pending.
        if (error.code === 'auth/invalid-action-code' ||
          error.message?.includes('invalid action') ||
          error.message?.includes('invalid request') ||
          error.message?.includes('missing initial state') ||
          error.message?.includes('Unable to process request') ||
          error.message?.includes('INTERNAL ASSERTION FAILED') ||
          error.message?.includes('Pending promise was never set')) {
          console.log('🔧 Auth state error detected - clearing stale auth data...');
          clearAllAuthData();
          console.log('✅ Cleared stale auth state - user can try signing in again');
        } else {
          setError(error.message || 'Failed to complete sign-in');
        }
        setLoading(false);
      }
    };

    handleRedirectResult();
  }, [onLoginSuccess]);

  const handleGoogleLogin = async () => {
    if (loading) return;

    setLoading(true);
    setLoadingProvider('google');
    setError(null);

    try {
      // Clear any existing auth state to prevent conflicts
      clearAllAuthData();

      // CRITICAL FIX: Use web-specific Firebase to avoid cross-platform conflicts
      const { auth } = initializeWebAuth();

      // Enhanced debugging: Verify we're using the correct web Firebase app
      console.log('🔍 WEB AUTH FIREBASE APP DEBUGGING:', {
        appName: auth.app.name,
        projectId: auth.app.options.projectId,
        authDomain: auth.app.options.authDomain,
        apiKey: auth.app.options.apiKey?.substring(0, 10) + '...',
        appId: auth.app.options.appId?.substring(0, 15) + '...',
        isWebSpecific: true,
        timestamp: new Date().toISOString()
      });

      // Debug: Verify Firebase configuration
      console.log('🔧 Firebase Configuration Debug:', {
        apiKey: auth.app.options.apiKey ? `${auth.app.options.apiKey.substring(0, 10)}...` : 'MISSING',
        authDomain: auth.app.options.authDomain || 'MISSING',
        projectId: auth.app.options.projectId || 'MISSING',
        storageBucket: auth.app.options.storageBucket || 'MISSING',
        messagingSenderId: auth.app.options.messagingSenderId || 'MISSING',
        appId: auth.app.options.appId || 'MISSING',
        measurementId: auth.app.options.measurementId || 'MISSING'
      });

      // Sign out any existing user first to ensure clean state
      if (auth.currentUser) {
        console.log('🔄 Signing out existing user before new authentication...');
        await auth.signOut();
      }

      // Use web-specific Google provider to avoid cross-platform conflicts
      const provider = getWebGoogleProvider();

      console.log('🔧 Google Provider Configuration:', {
        scopes: ['email', 'profile'],
        providerId: provider.providerId,
        configured: 'Provider configured successfully'
      });

      console.log('🔄 Starting Google authentication...');
      console.log('🔧 Auth configuration:', {
        projectId: auth.app.options.projectId,
        authDomain: auth.app.options.authDomain,
        currentUser: auth.currentUser?.uid || 'none'
      });

      // Enhanced debugging: Check Firebase app instance details
      console.log('🔍 OAUTH FIREBASE APP DEBUGGING:', {
        appName: auth.app.name,
        projectId: auth.app.options.projectId,
        authDomain: auth.app.options.authDomain,
        apiKey: auth.app.options.apiKey?.substring(0, 10) + '...',
        appId: auth.app.options.appId?.substring(0, 15) + '...',
        timestamp: new Date().toISOString()
      });

      // Validate that Google provider is properly configured
      try {
        // Check if we can access the Google provider configuration
        const testProvider = new GoogleAuthProvider();
        console.log('🔧 Google Provider Validation:', {
          providerId: testProvider.providerId,
          canAddScopes: typeof testProvider.addScope === 'function',
          canSetParams: typeof testProvider.setCustomParameters === 'function'
        });
      } catch (providerError) {
        console.error('🚨 Google Provider Configuration Error:', providerError);
        throw new Error('Google authentication provider is not properly configured');
      }

      // Check for native platform - logic for specific handling
      if (isNative()) {
        console.log('📱 Native platform detected: Using native Google Sign-In plugin');
        try {
          // Pass configuration options to the native plugin
          const result = await FirebaseAuthentication.signInWithGoogle({
            mode: 'redirect', // Use redirect mode for iOS
            scopes: ['email', 'profile'],
            webClientId: process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '418607615172-qjkm9cpud0ghklds8nv6ajtu1v80ogn7.apps.googleusercontent.com'
          });

          console.log('🔍 Native sign-in result:', {
            hasCredential: !!result.credential,
            hasIdToken: !!result.credential?.idToken,
            hasUser: !!result.user
          });

          if (result.credential?.idToken) {
             const credential = GoogleAuthProvider.credential(result.credential.idToken);
             const userCredential = await signInWithCredential(auth, credential);

             console.log('🎉 Native authentication successful!', {
              email: userCredential.user.email,
              uid: userCredential.user.uid
             });

             onLoginSuccess(userCredential.user);
             return;
          } else if (result.user?.uid) {
             // If the plugin returns a user but no credential, we need to get the proper Firebase user
             // by waiting for the auth state to update
             console.log('🔍 Native plugin returned user without credential, waiting for auth state...');

             // The native plugin should have already signed in to Firebase
             // Just wait for the auth state to update
             const currentUser = auth.currentUser;
             if (currentUser) {
               console.log('🎉 Native authentication successful (from auth state)!', {
                 email: currentUser.email,
                 uid: currentUser.uid
               });
               onLoginSuccess(currentUser);
               return;
             }

             throw new Error('Native sign-in returned user but Firebase auth state not updated');
          } else {
             throw new Error('Native sign-in succeeded but no ID token or user returned');
          }
        } catch (nativeError: any) {
           console.error('🚨 Native sign-in failed:', nativeError);
           console.error('🚨 Error details:', {
             code: nativeError.code,
             message: nativeError.message,
             stack: nativeError.stack?.substring(0, 200)
           });

           // Fallback to redirect if native plugin fails (e.g. not configured)
           if (nativeError.message?.includes('Unimplemented') ||
               nativeError.message?.includes('plugin not installed') ||
               nativeError.code === 'UNIMPLEMENTED') {
              console.log('⚠️ Native plugin issue, falling back to redirect auth...');
              await signInWithRedirect(auth, provider);
              return;
           }

           throw nativeError;
        }
      }

      // Use popup authentication with redirect fallback
      console.log('🔄 Attempting popup authentication...');

      try {
        const result = await signInWithPopup(auth, provider);

        if (result?.user) {
          console.log('🎉 Popup authentication successful!', {
            email: result.user.email,
            uid: result.user.uid,
            displayName: result.user.displayName
          });
          onLoginSuccess(result.user);
          return;
        } else {
          throw new Error('Authentication succeeded but no user data received');
        }
      } catch (popupError: any) {
        console.warn('⚠️ Popup authentication failed:', popupError.code, popupError.message);

        // Check if sessionStorage is working before attempting redirect
        const isSessionStorageAvailable = (() => {
          try {
            const testKey = '__firebase_test__';
            sessionStorage.setItem(testKey, 'test');
            const retrieved = sessionStorage.getItem(testKey);
            sessionStorage.removeItem(testKey);
            return retrieved === 'test';
          } catch {
            return false;
          }
        })();

        // If OAuth client configuration issue or missing fields, fallback to redirect
        // BUT only if sessionStorage is available (otherwise redirect will fail)
        if (popupError.message?.includes('missing required data') ||
          popupError.message?.includes('missing fields') ||
          popupError.message?.includes('undefined is not an object') ||
          popupError.message?.includes('gapi.iframes.getContext') ||
          popupError.code === 'auth/invalid-oauth-client-id' ||
          popupError.code === 'auth/unauthorized-domain' ||
          popupError.code === 'auth/popup-closed-by-user') {

          if (isNative()) {
            console.error('🚨 GAPI/OAuth issue detected on native - redirect will likely fail.');
            setError('Sign-in could not be completed because the browser environment is restricted. Please use the native sign-in dialog or check your browser settings.');
            return;
          }

          if (!isSessionStorageAvailable) {
            console.warn('⚠️ sessionStorage not available - cannot use redirect auth');
            throw new Error('Please enable cookies/storage in your browser settings, or try a different browser.');
          }

          console.log('🔄 OAuth configuration or GAPI issue detected, trying redirect authentication...');
          await signInWithRedirect(auth, provider);
          console.log('✅ Redirect initiated - user will be redirected for authentication');
          return;
        }

        // For other errors, re-throw
        throw popupError;
      }
    } catch (error: any) {
      console.error('🚨 Login error details:', {
        code: error.code,
        message: error.message,
        customData: error.customData,
        stack: error.stack?.substring(0, 500) + '...'
      });

      // Enhanced error analysis
      console.error('🔍 Error Analysis:', {
        type: typeof error,
        constructor: error.constructor.name,
        code: error.code,
        message: error.message,
        stack: error.stack?.substring(0, 300),
        customData: error.customData,
        hasAuth: 'auth' in error,
        hasCredential: 'credential' in error
      });

      // Handle specific error cases
      if (error.message?.includes('Request is missing required data') ||
        error.message?.includes('missing required data') ||
        error.message?.includes('missing fields')) {
        console.error('🚨 OAUTH CLIENT ERROR: This indicates Google OAuth client configuration issue');
        console.error('🔧 Attempting redirect authentication as fallback...');
        setError('OAuth configuration issue detected. Redirecting to alternative sign-in method...');

        // Try redirect as last resort using web-specific Firebase
        try {
          const auth = getWebAuth();
          const provider = getWebGoogleProvider();
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error('🚨 Redirect fallback also failed:', redirectError);
          setError('Authentication system temporarily unavailable. Please try again later.');
        }
      } else if (error.message?.includes('popup_closed_by_user') ||
        error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked. Please allow pop-ups and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized. Please contact support.');
        console.error('🚨 DOMAIN ERROR: Add this domain to Firebase Console → Authentication → Settings → Authorized domains');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google sign-in is not enabled. Please contact support.');
        console.error('🚨 CONFIG ERROR: Enable Google provider in Firebase Console → Authentication → Sign-in method');
      } else if (error.code === 'auth/configuration-not-found') {
        setError('Firebase configuration error. Please contact support.');
        console.error('🚨 CONFIG ERROR: Check Firebase project configuration');
      } else if (error.code === 'auth/invalid-oauth-client-id') {
        setError('OAuth client configuration error. Trying alternative method...');
        console.error('🚨 OAUTH CLIENT ID ERROR: Check Google OAuth client configuration in Firebase Console');

        // Try redirect as fallback for OAuth client issues using web-specific Firebase
        try {
          const auth = getWebAuth();
          const provider = getWebGoogleProvider();
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error('🚨 Redirect fallback failed:', redirectError);
        }
      } else if (error.code === 'auth/invalid-action-code' ||
        error.message?.includes('invalid action')) {
        console.error('🚨 INVALID ACTION: OAuth state mismatch detected');
        setError('Authentication session expired. Please try again.');
      } else {
        console.error('🚨 Unexpected login error:', error);
        setError(`Authentication failed: ${error.message || error.code || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
      setLoadingProvider(null);
    }
  };

  const handleEmailLogin = async () => {
    if (loading) return;

    // Validate inputs
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setLoadingProvider('email');
    setError(null);

    try {
      // Clear any existing auth state
      clearAllAuthData();

      const { auth } = initializeWebAuth();

      console.log('📧 Starting email/password authentication...');

      // Sign out any existing user first
      if (auth.currentUser) {
        console.log('🔄 Signing out existing user before new authentication...');
        await auth.signOut();
      }

      // Sign in with email and password
      const result = await signInWithEmailAndPassword(auth, email, password);

      if (result?.user) {
        console.log('🎉 Email authentication successful!', {
          email: result.user.email,
          uid: result.user.uid
        });
        onLoginSuccess(result.user);
        return;
      } else {
        throw new Error('Authentication succeeded but no user data received');
      }
    } catch (error: any) {
      console.error('🚨 Email login error:', {
        code: error.code,
        message: error.message
      });

      // Handle specific error cases
      if (error.code === 'auth/user-not-found') {
        // User doesn't exist - block new signups via email/password
        setError('Email/password login is only available for existing users. Please sign in with Google or Apple instead.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (error.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/invalid-credential') {
        // This error can mean either wrong password or user not found
        setError('Invalid email or password. Please sign in with Google or Apple instead.');
      } else {
        setError(`Authentication failed: ${error.message || error.code || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
      setLoadingProvider(null);
    }
  };

  const handleAppleLogin = async () => {
    if (loading) return;

    setLoading(true);
    setLoadingProvider('apple');
    setError(null);

    try {
      // Clear any existing auth state to prevent conflicts
      clearAllAuthData();

      const { auth } = initializeWebAuth();

      console.log('🍎 Starting Apple authentication...');

      // Sign out any existing user first to ensure clean state
      if (auth.currentUser) {
        console.log('🔄 Signing out existing user before new authentication...');
        await auth.signOut();
      }

      // Check for native platform - use native Apple Sign-In plugin
      if (isNative() && isIOS()) {
        console.log('📱 Native iOS platform detected: Using native Apple Sign-In plugin');
        try {
          // skipNativeAuth: false — let the native plugin sign into Firebase natively (persists to iOS Keychain).
          // We still bridge the credential into the web SDK so the JS layer has a session too.
          const result = await FirebaseAuthentication.signInWithApple({
            scopes: ['email', 'name'],
            skipNativeAuth: false,
          });

          console.log('🔍 Native Apple sign-in result:', {
            hasCredential: !!result.credential,
            hasIdToken: !!result.credential?.idToken,
            hasUser: !!result.user
          });

          if (result.credential?.idToken) {
            const provider = new OAuthProvider('apple.com');
            const credential = provider.credential({
              idToken: result.credential.idToken,
              rawNonce: result.credential.nonce || undefined
            });
            const userCredential = await signInWithCredential(auth, credential);

            console.log('🎉 Native Apple authentication successful!', {
              email: userCredential.user.email,
              uid: userCredential.user.uid
            });

            onLoginSuccess(userCredential.user);
            return;
          } else if (result.user?.uid) {
            // If the plugin returns a user but no credential, wait for auth state
            console.log('🔍 Native plugin returned user without credential, waiting for auth state...');
            const currentUser = auth.currentUser;
            if (currentUser) {
              console.log('🎉 Native Apple authentication successful (from auth state)!', {
                email: currentUser.email,
                uid: currentUser.uid
              });
              onLoginSuccess(currentUser);
              return;
            }
            throw new Error('Native Apple sign-in returned user but Firebase auth state not updated');
          } else {
            throw new Error('Native Apple sign-in succeeded but no ID token or user returned');
          }
        } catch (nativeError: any) {
          console.error('🚨 Native Apple sign-in failed:', nativeError);
          console.error('🚨 Error details:', {
            code: nativeError.code,
            message: nativeError.message,
            stack: nativeError.stack?.substring(0, 200)
          });

          // If user cancelled, show friendly message
          if (nativeError.code === 'ERR_CANCELED' || nativeError.message?.includes('canceled')) {
            setError('Sign-in was cancelled. Please try again.');
            return;
          }

          // Fallback to web-based Apple Sign-In ONLY if plugin is missing/unimplemented
          if (nativeError.message?.includes('Unimplemented') || 
              nativeError.message?.includes('plugin not installed') ||
              nativeError.code === 'UNIMPLEMENTED') {
            console.log('⚠️ Native Apple Sign-In plugin missing, falling back to web...');
          } else {
            // For other native errors (e.g. configuration), web fallback will likely fail or cause GAPI errors
            console.error('🚨 Native Apple Sign-In failed due to configuration or environment.');
            setError(`Apple Sign-In failed: ${nativeError.message || 'Check your device settings'}`);
            return;
          }
        }
      }

      // Web-based Apple Sign-In
      const provider = getWebAppleProvider();

      try {
        const result = await signInWithPopup(auth, provider);

        if (result?.user) {
          console.log('🎉 Apple popup authentication successful!', {
            email: result.user.email,
            uid: result.user.uid,
            displayName: result.user.displayName
          });
          onLoginSuccess(result.user);
          return;
        } else {
          throw new Error('Authentication succeeded but no user data received');
        }
      } catch (popupError: any) {
        console.warn('⚠️ Apple popup authentication failed:', popupError.code, popupError.message);

        // Handle popup closed by user
        if (popupError.code === 'auth/popup-closed-by-user') {
          setError('Sign-in was cancelled. Please try again.');
          return;
        }

        // Catch GAPI / iframe context errors which are common in restricted environments like Capacitor WebViews
        if (popupError.message?.includes('undefined is not an object') ||
            popupError.message?.includes('gapi.iframes.getContext')) {
          console.error('🚨 GAPI error detected in Apple popup flow');
          setError('Sign-in could not be completed because the browser environment is restricted. Please use the native sign-in dialog or check your browser settings.');
          return;
        }

        // Try redirect as fallback ONLY if not on native platforms
        if (!isNative() && (popupError.code === 'auth/popup-blocked' || popupError.code === 'auth/cancelled-popup-request')) {
          console.log('🔄 Popup blocked, trying redirect authentication...');
          await signInWithRedirect(auth, provider);
          return;
        }

        throw popupError;
      }
    } catch (error: any) {
      console.error('🚨 Apple login error:', {
        code: error.code,
        message: error.message
      });

      if (error.code === 'auth/popup-closed-by-user' || error.message?.includes('canceled')) {
        setError('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized. Please contact support.');
      } else if (error.message?.includes('gapi.iframes.getContext') || error.message?.includes('undefined is not an object')) {
        setError('Sign-in unsuccessful: Browser environment restricted (GAPI error). Please try again or use a different sign-in method.');
      } else {
        setError(`Authentication failed: ${error.message || error.code || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
      setLoadingProvider(null);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: theme.palette.background.default,
      p: { xs: 2, sm: 3, md: 4 },
      textAlign: 'center',
    }}>
      {/* Logo and App Name */}
      <Box sx={{
        mb: { xs: 1, sm: 2, md: 3 },
      }}>
        <Logo
          size="xlarge"
          variant="default"
          state="animated"
        />
      </Box>

      {/* Welcome Text */}
      <Typography sx={{
        fontSize: { xs: 16, sm: 18 },
        fontWeight: 400,
        color: theme.palette.text.secondary,
        mb: { xs: 4, sm: 6 },
        maxWidth: { xs: 280, sm: 300, md: 350 },
        fontFamily: font(),
        px: { xs: 1, sm: 0 },
      }}>
        Discover, organize, and share your favorite movies
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{
          mb: 3,
          maxWidth: { xs: '100%', sm: 400 },
          width: '100%',
        }}>
          {error}
        </Alert>
      )}

      {/* Sign In Buttons Container */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: { xs: 280, sm: 300 },
        mb: 3,
      }}>
        {/* Google Sign In Button */}
        <Button
          data-testid="login-with-google"
          variant="contained"
          size="large"
          startIcon={loadingProvider === 'google' ? <CircularProgress size={20} color="inherit" /> : <Google />}
          onClick={handleGoogleLogin}
          disabled={loading}
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? '#3f3db2' : '#000000',
            color: '#FFFFFF',
            borderRadius: 3,
            py: { xs: 1.25, sm: 1.5 },
            px: { xs: 3, sm: 4 },
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: font(),
            width: '100%',
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            },
            '&:disabled': {
              bgcolor: `${theme.palette.primary.main}80`,
              color: '#FFFFFF',
            },
          }}
        >
          {loadingProvider === 'google' ? 'Signing in...' : 'Continue with Google'}
        </Button>

        {/* Apple Sign In Button - shown on iOS/macOS */}
        {showAppleSignIn() && (
          <Button
            data-testid="login-with-apple"
            variant="contained"
            size="large"
            startIcon={loadingProvider === 'apple' ? <CircularProgress size={20} color="inherit" /> : <Apple />}
            onClick={handleAppleLogin}
            disabled={loading}
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#000000',
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#FFFFFF',
              borderRadius: 3,
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: 14, sm: 16 },
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: font(),
              width: '100%',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#333333',
              },
              '&:disabled': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
              },
            }}
          >
            {loadingProvider === 'apple' ? 'Signing in...' : 'Continue with Apple'}
          </Button>
        )}
      </Box>

      {/* Email/Password Login Section (Collapsible) */}
      {<Box sx={{
        width: '100%',
        maxWidth: { xs: 280, sm: 300 },
        mb: 2,
      }}>
        <Button
          onClick={() => setShowEmailLogin(!showEmailLogin)}
          fullWidth
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 500,
            '&:hover': {
              bgcolor: 'transparent',
              color: theme.palette.text.primary,
            },
          }}
          endIcon={<ExpandMore sx={{
            transform: showEmailLogin ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }} />}
        >
          Sign in with Email
        </Button>

        <Collapse in={showEmailLogin}>
          <Box sx={{
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Typography sx={{
              fontSize: 12,
              color: theme.palette.text.secondary,
              textAlign: 'center',
              fontStyle: 'italic',
            }}>
              For existing email/password accounts only.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
              variant="outlined"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleEmailLogin();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={loadingProvider === 'email' ? <CircularProgress size={20} color="inherit" /> : <Email />}
              onClick={handleEmailLogin}
              disabled={loading}
              fullWidth
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#424242',
                color: '#FFFFFF',
                borderRadius: 2,
                py: 1,
                fontSize: 14,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? '#3d3d3d' : '#525252',
                },
                '&:disabled': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(45,45,45,0.5)' : 'rgba(66,66,66,0.5)',
                  color: '#FFFFFF',
                },
              }}
            >
              {loadingProvider === 'email' ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        </Collapse>
      </Box>}

      {/* Footer */}
      <Typography sx={{
        fontSize: { xs: 11, sm: 12 },
        color: theme.palette.text.disabled,
        mt: { xs: 4, sm: 6 },
        maxWidth: { xs: 280, sm: 300 },
        fontFamily: font(),
        px: { xs: 1, sm: 0 },
      }}>
        By continuing, you agree to our{' '}
        <a
          href="https://watchlistcrew.com/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Terms of Service
        </a>
        {' '}and{' '}
        <a
          href="https://watchlistcrew.com/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Privacy Policy
        </a>
      </Typography>
    </Box>
  );
};

export default LoginScreen;